/**
 * @button 模式面板：选区回填、关面板提交（对齐 Chart useChartPopoverSelection）
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useMemoizedFn } from 'ahooks';
import type { RegionSelectValueType } from '@home/components/NewRegionSelect/type';
import type { IKnowledgeBaseItem, IKnowledgeDocItem } from './useKnowledgeBaseApi';
import {
  getKnowledgeTagKey,
  type ISelectedKnowledgeTagData,
} from '../components/SelectedKnowledgeTag';
import type { TKnowledgeMentionOpenReason } from './useKnowledgeMentionHost';
import {
  KB_EMPTY_VALUE,
  derivePanelSelectValue,
  isPanelRestoreDocsReady,
  isRegionSelectValueChanged,
  resolveCommitKnowledgeTags,
  resolveDefaultPanelKbId,
  resolveKbIdsNeedingDocsForDocNameRestore,
  resolveKbIdsNeedingDocsForPanelRestore,
} from '../utils/kbSelectionUtils';

interface IUseKbButtonPanelParams {
  opened: boolean;
  setOpened: (open: boolean) => void;
  openReason: TKnowledgeMentionOpenReason;
  isAtMode: boolean;
  selectedItems: ISelectedKnowledgeTagData[];
  kbList: IKnowledgeBaseItem[];
  docsByKbId: Record<string, IKnowledgeDocItem[]>;
  kbLoading: boolean;
  panelKbList: IKnowledgeBaseItem[];
  loadKbDocs: (ids: string[]) => void;
  onSelectedChange?: (items: ISelectedKnowledgeTagData[]) => void;
  onPanelSync?: (nextItems: ISelectedKnowledgeTagData[]) => void;
  onPanelCommit?: () => void;
  onBeforePanelOpen?: () => void;
  onOpenAsButton?: () => void;
  hoveredRef: React.MutableRefObject<boolean>;
}

export const useKbButtonPanel = ({
  opened,
  setOpened,
  openReason,
  isAtMode,
  selectedItems,
  kbList,
  docsByKbId,
  kbLoading,
  panelKbList,
  loadKbDocs,
  onSelectedChange,
  onPanelSync,
  onPanelCommit,
  onOpenAsButton,
  hoveredRef,
}: IUseKbButtonPanelParams) => {
  const [localSelectValue, setLocalSelectValue] =
    useState<RegionSelectValueType>(KB_EMPTY_VALUE);
  const [defaultSelectedRegion, setDefaultSelectedRegion] = useState('');
  const [regionMountKey, setRegionMountKey] = useState(0);

  const openedRef = useRef(false);
  const interactedRef = useRef(false);
  const selectionTouchedRef = useRef(false);
  const localSelectValueRef = useRef<RegionSelectValueType>(KB_EMPTY_VALUE);
  const allKbListRef = useRef<IKnowledgeBaseItem[]>([]);
  const docsByKbIdRef = useRef<Record<string, IKnowledgeDocItem[]>>({});
  const lastSyncKeyRef = useRef('');
  const lastAppliedSelectedItemsKeyRef = useRef('');
  const prevOpenedRef = useRef(false);
  const panelListReadyRef = useRef(false);
  const panelRestoreDocsReadyRef = useRef(false);

  const selectedItemsKey = useMemo(
    () => selectedItems.map((item) => getKnowledgeTagKey(item)).sort().join('|'),
    [selectedItems],
  );

  const panelRestoreDocsKey = useMemo(() => {
    return resolveKbIdsNeedingDocsForPanelRestore(selectedItems)
      .map((kbId) => `${kbId}:${docsByKbId[kbId]?.length ?? 'pending'}`)
      .join('|');
  }, [selectedItems, docsByKbId]);

  useEffect(() => {
    if (kbList.length) {
      allKbListRef.current = kbList;
    }
  }, [kbList]);

  useEffect(() => {
    docsByKbIdRef.current = docsByKbId;
  }, [docsByKbId]);

  useEffect(() => {
    localSelectValueRef.current = localSelectValue;
  });

  /** 从 selectedItems 回填：整库 regions 立即可用；仅 docName 待解析时等文档 */
  const applyPanelSelection = useMemoizedFn(() => {
    if (!opened || openReason !== 'button') return;
    if (kbLoading) return;

    if (selectionTouchedRef.current) {
      if (selectedItemsKey === lastAppliedSelectedItemsKeyRef.current) {
        return;
      }
    }

    const syncKey = `${selectedItemsKey}|${panelRestoreDocsKey}`;
    if (lastSyncKeyRef.current === syncKey) {
      return;
    }

    const nextValue = selectedItems.length
      ? derivePanelSelectValue(
        selectedItems,
        allKbListRef.current,
        docsByKbIdRef.current,
      )
      : KB_EMPTY_VALUE;

    const prevValue = localSelectValueRef.current;
    lastSyncKeyRef.current = syncKey;
    lastAppliedSelectedItemsKeyRef.current = selectedItemsKey;
    localSelectValueRef.current = nextValue;
    setLocalSelectValue(nextValue);

    if (isRegionSelectValueChanged(prevValue, nextValue)) {
      setRegionMountKey((key) => key + 1);
    }
  });

  useEffect(() => {
    if (opened && !prevOpenedRef.current) {
      interactedRef.current = false;
      selectionTouchedRef.current = false;
      lastSyncKeyRef.current = '';
      lastAppliedSelectedItemsKeyRef.current = '';
    }
    if (!opened && prevOpenedRef.current) {
      setRegionMountKey((key) => key + 1);
    }
    prevOpenedRef.current = opened;
    if (!opened) {
      lastSyncKeyRef.current = '';
    }
  }, [opened]);

  useEffect(() => {
    if (!opened) return;
    applyPanelSelection();
  }, [
    opened,
    selectedItemsKey,
    kbLoading,
    panelRestoreDocsKey,
    applyPanelSelection,
  ]);

  useEffect(() => {
    if (!opened || kbLoading) {
      return;
    }
    const list = allKbListRef.current.length ? allKbListRef.current : kbList;
    setDefaultSelectedRegion(resolveDefaultPanelKbId(selectedItems, list));
  }, [opened, kbLoading, kbList, selectedItemsKey, selectedItems]);

  const handleLocalSelectChange = useCallback((next: RegionSelectValueType) => {
    interactedRef.current = true;
    selectionTouchedRef.current = true;
    localSelectValueRef.current = next;
    setLocalSelectValue(next);
  }, []);

  const commitButtonPanel = useMemoizedFn((
    overrideValue?: RegionSelectValueType,
  ) => {
    if (!selectionTouchedRef.current || openReason !== 'button') {
      onPanelCommit?.();
      return;
    }

    const nextTags = resolveCommitKnowledgeTags(
      overrideValue ?? localSelectValueRef.current,
      allKbListRef.current,
      selectedItems,
    );

    unstable_batchedUpdates(() => {
      onPanelSync?.(nextTags);
      onSelectedChange?.(nextTags);
    });
    onPanelCommit?.();
  });

  const closePanel = useMemoizedFn((overrideValue?: RegionSelectValueType) => {
    if (!openedRef.current) return;
    openedRef.current = false;
    commitButtonPanel(overrideValue);
    interactedRef.current = false;
    setOpened(false);
  });

  useEffect(() => {
    if (opened) {
      openedRef.current = true;
      return;
    }
    if (!openedRef.current) return;
    openedRef.current = false;
    commitButtonPanel();
    interactedRef.current = false;
  }, [opened, commitButtonPanel]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (nextOpen) {
      openedRef.current = true;
      if (!opened) onOpenAsButton?.();
      return;
    }
    if (hoveredRef.current && !interactedRef.current) return;
    setOpened(false);
  }, [opened, onOpenAsButton, hoveredRef, setOpened]);

  const handleRegionSelectClose = useCallback((result: RegionSelectValueType) => {
    if (!openedRef.current) return;
    if (hoveredRef.current && !interactedRef.current) return;
    interactedRef.current = true;
    selectionTouchedRef.current = true;
    localSelectValueRef.current = result;
    setLocalSelectValue(result);
    closePanel(result);
  }, [hoveredRef, closePanel]);

  useEffect(() => {
    if (!opened || isAtMode) {
      panelListReadyRef.current = false;
      return;
    }
    if (panelKbList.length > 0 && !panelListReadyRef.current) {
      panelListReadyRef.current = true;
      setRegionMountKey((key) => key + 1);
    }
  }, [opened, isAtMode, panelKbList.length]);

  useEffect(() => {
    if (!opened || isAtMode) {
      return;
    }
    const kbIds = resolveKbIdsNeedingDocsForPanelRestore(selectedItems);
    if (kbIds.length) {
      loadKbDocs(kbIds);
    }
  }, [opened, isAtMode, selectedItems, loadKbDocs]);

  useEffect(() => {
    if (!opened) {
      panelRestoreDocsReadyRef.current = false;
      return;
    }
    if (isAtMode || !selectedItems.length) {
      return;
    }
    if (!resolveKbIdsNeedingDocsForDocNameRestore(selectedItems).length) {
      return;
    }
    const ready = isPanelRestoreDocsReady(selectedItems, docsByKbId);
    if (ready && !panelRestoreDocsReadyRef.current) {
      panelRestoreDocsReadyRef.current = true;
      setRegionMountKey((key) => key + 1);
    }
  }, [opened, isAtMode, selectedItems, docsByKbId]);

  const bumpRegionMountKey = useMemoizedFn(() => {
    setRegionMountKey((key) => key + 1);
  });

  return {
    localSelectValue,
    localSelectValueRef,
    defaultSelectedRegion,
    regionMountKey,
    openedRef,
    interactedRef,
    handleLocalSelectChange,
    handleOpenChange,
    handleRegionSelectClose,
    closePanel,
    bumpRegionMountKey,
  };
};
